import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Brush, CartesianGrid, Label, Legend, Line, LineChart, XAxis, YAxis, ComposedChart, Tooltip, ReferenceLine } from 'recharts';
import i18next from 'i18next';

import GraphUtility from './graph_utility';
import { UserUnitService } from '../../services';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import CustomTooltip from './custom_tooltip';

const graphConfig = {
    MAIN_GRAPH_WIDTH: 550,
    BRUSH_GRAPH_WIDTH: 550,
    BRUSH_GRAPH_HEIGHT: 100,
    GRAPH_X_HEIGHT: 50,
    BRUSH_HEIGHT: 100 - 50
};

const SimpleLineGraph = connect(({ user }) => ({ me: user.me }))(({ me, rawData, displayName, dataTypeLabel, timezone, hideBrush, setPoints }) => {
    const data = useMemo(() => {
        return rawData
                    .toSorted((a, b) => a.timestamp - b.timestamp)
                    .map((dp) => {
                        let utcOffset = timezone ? moment(dp.timestamp).tz(timezone).utcOffset() : moment(dp.timestamp).utcOffset();
                        return {
                            timestamp: Math.round(dp.timestamp / 1000) + utcOffset * 60,
                            reading: UserUnitService.convertFromSystemUnitToUserUnit(me.temperatureDisplayUnit, dp.reading),
                        };
                    });
    }, [rawData]);
    const lastIndex = useMemo(() => data.length - 1, [data]);

    const [startIndex, setStartIndex] = useState(0);
    const [endIndex, setEndIndex] = useState(lastIndex);

    const [mainTicks, setMainTicks] = useState(GraphUtility.calculateTicks(data[startIndex].timestamp, data[endIndex].timestamp))
    const [mainDomain, setMainDomain] = useState([data[startIndex].timestamp, data[endIndex].timestamp]);

    const strokeColors = ["#4287f5", "#42f57b", "#bcf542", "#42ddf5"];

    useEffect(() => {
        setMainTicks(GraphUtility.calculateTicks(data[startIndex].timestamp, data[endIndex].timestamp));
        setMainDomain([data[startIndex].timestamp, data[endIndex].timestamp]);
    }, [startIndex, endIndex]);

    return (
        <div style={{paddingTop: 10}}>
            <LineChart width={graphConfig.MAIN_GRAPH_WIDTH} height={250} data={data}>
                <CartesianGrid strokeDasharray="0 0" />
                <Tooltip content={<CustomTooltip graphType="line" external={window.external} />}/>

                <Legend 
                    width={graphConfig.MAIN_GRAPH_WIDTH} 
                    layout="horizontal" 
                    align="left" 
                    verticalAlign="top" 
                    wrapperStyle={{ fontSize: 12, top: "-15px" }}
                    icon="plainline" 
                />

                <XAxis
                    type="number"
                    dataKey="timestamp"
                    scale="time"
                    ticks={mainTicks}
                    domain={mainDomain}
                    allowDataOverflow={true}
                    tickFormatter={(tick) => GraphUtility.formatTicks(tick, mainDomain[0], mainDomain[1])}
                    height={graphConfig.GRAPH_X_HEIGHT}
                >
                    <Label value={i18next.t("graphing.date-time")} offset={10} position="insideBottom" />
                </XAxis>
                <YAxis type="number" allowDecimals={true} domain={["auto", "auto"]} label={{ value: dataTypeLabel, angle: -90, position: "insideLeft" }} width={80}></YAxis>

                <Line connectNulls type="step" dot={false} dataKey="reading" name={displayName} stroke="#ffa500" strokeWidth={2} isAnimationActive={false} />

                { setPoints && setPoints.map((value, i) => <ReferenceLine y={value} stroke={i < strokeColors.length ? strokeColors[i] : strokeColors[0]} strokeOpacity={0.5} isFront={true} strokeWidth={3} ifOverflow="extendDomain"/>) }

            </LineChart>

            {(hideBrush != true) && <div className="chart-brush-container">
                <div>
                    <LineChart width={graphConfig.BRUSH_GRAPH_WIDTH} height={graphConfig.BRUSH_GRAPH_HEIGHT} data={data}>
                        <CartesianGrid strokeDasharray="0 0" />
                        <XAxis
                            type="number"
                            dataKey="timestamp"
                            scale="time"
                            ticks={GraphUtility.calculateTicks(data[0].timestamp, data[lastIndex].timestamp)}
                            domain={[data[0].timestamp, data[lastIndex].timestamp]}
                            tickFormatter={(tick) => GraphUtility.formatTicks(tick, data[0].timestamp, data[lastIndex].timestamp)}
                            height={graphConfig.GRAPH_X_HEIGHT}
                        >
                            <Label value={i18next.t("graphing.date-time")} offset={10} position="insideBottom" />
                        </XAxis>
                        <YAxis type="number" allowDecimals={false} domain={["auto", "auto"]} padding={{ top: 15, bottom: 15 }} width={80}></YAxis>

                        <Line connectNulls type="step" dot={false} dataKey="reading" name={displayName} stroke="#ffa500" strokeWidth={2} isAnimationActive={false} />
                        
                    </LineChart>
                </div>
                <div className="chart-brush">
                    <ComposedChart width={graphConfig.BRUSH_GRAPH_WIDTH} height={graphConfig.BRUSH_HEIGHT} data={data}>
                        <XAxis type="number" dataKey="timestamp" scale="time" domain={[data[0].timestamp, data[lastIndex].timestamp]} height={-60} />
                        <YAxis type="number" domain={["auto", "auto"]} width={80}></YAxis>
                        <Brush
                            height={graphConfig.BRUSH_HEIGHT}
                            dataKey="timestamp"
                            startIndex={startIndex}
                            endIndex={endIndex}
                            fill="rgba(0, 0, 0, 0)"
                            stroke="gray"
                            onChange={({ startIndex, endIndex }) => {
                                setStartIndex(startIndex);
                                setEndIndex(endIndex);
                            }}
                            tickFormatter={() => ""}
                        />
                    </ComposedChart>
                </div>
            </div>}
        </div>
    );
});

export default SimpleLineGraph;